/**
 * Block -> sound material (vanilla SoundType), resolved from the block name so
 * blocks other sessions add pick up a sensible group without a table edit.
 * Names follow vanilla's sound groups: block.<material>.break/place/step/hit/fall.
 */
import { BLOCKS } from '../blocks';

export const MATERIALS = [
  'stone', 'wood', 'grass', 'gravel', 'sand', 'snow', 'cloth', 'glass', 'metal',
  'ladder', 'slime', 'honey', 'soul_sand', 'soul_soil', 'netherrack', 'nether_bricks', 'nether_ore', 'nylium',
  'stem', 'wart_block', 'basalt', 'bone_block', 'chain', 'lantern', 'anvil', 'coral', 'bamboo', 'scaffolding',
  'sweet_berry_bush', 'crop', 'azalea', 'azalea_leaves', 'moss', 'big_dripleaf', 'cave_vines', 'deepslate',
  'deepslate_bricks', 'tuff', 'calcite', 'dripstone', 'pointed_dripstone', 'copper', 'amethyst', 'froglight',
  'sculk', 'mud', 'rooted_dirt', 'powder_snow', 'ancient_debris', 'shroomlight', 'wet_grass', 'lily_pad',
  'hanging_roots', 'cherry_wood', 'cherry_leaves', 'nether_wood', 'bamboo_wood', 'mangrove_roots', 'packed_mud',
] as const;
export type Material = typeof MATERIALS[number];

/** Ordered keyword rules; first match wins (default stone). */
const RULES: [RegExp, Material][] = [
  [/glowstone|sea lantern|glass|^ice$|packed ice|blue ice|beacon/, 'glass'],
  [/soul sand/, 'soul_sand'],
  [/soul soil/, 'soul_soil'],
  [/nether gold ore|nether quartz ore/, 'nether_ore'],
  [/netherrack/, 'netherrack'],
  [/nether brick/, 'nether_bricks'],
  [/nylium/, 'nylium'],
  [/crimson stem|warped stem|hyphae/, 'stem'],
  [/wart block/, 'wart_block'],
  [/crimson|warped/, 'nether_wood'],
  [/basalt/, 'basalt'],
  [/ancient debris/, 'ancient_debris'],
  [/shroomlight/, 'shroomlight'],
  [/froglight/, 'froglight'],
  [/bone block/, 'bone_block'],
  [/chain/, 'chain'],
  [/lantern/, 'lantern'],
  [/anvil/, 'anvil'],
  [/coral/, 'coral'],
  [/scaffolding/, 'scaffolding'],
  [/bamboo (planks|block|mosaic)/, 'bamboo_wood'],
  [/bamboo/, 'bamboo'],
  [/sweet berry/, 'sweet_berry_bush'],
  [/wheat|carrots|potatoes|beetroot|nether wart$/, 'crop'],
  [/azalea leaves/, 'azalea_leaves'],
  [/azalea/, 'azalea'],
  [/moss/, 'moss'],
  [/big dripleaf/, 'big_dripleaf'],
  [/cave vines/, 'cave_vines'],
  [/hanging roots/, 'hanging_roots'],
  [/deepslate (bricks|tiles)/, 'deepslate_bricks'],
  [/deepslate/, 'deepslate'],
  [/tuff/, 'tuff'],
  [/calcite/, 'calcite'],
  [/pointed dripstone/, 'pointed_dripstone'],
  [/dripstone/, 'dripstone'],
  [/amethyst/, 'amethyst'],
  [/sculk/, 'sculk'],
  [/packed mud|mud brick/, 'packed_mud'],
  [/mangrove roots/, 'mangrove_roots'],
  [/^mud$/, 'mud'],
  [/rooted dirt/, 'rooted_dirt'],
  [/powder snow/, 'powder_snow'],
  [/slime/, 'slime'],
  [/honey/, 'honey'],
  [/ladder/, 'ladder'],
  [/lily pad/, 'lily_pad'],
  [/kelp|seagrass/, 'wet_grass'],
  [/cherry (planks|log|wood)/, 'cherry_wood'],
  [/cherry leaves|pink petals/, 'cherry_leaves'],
  [/\bore\b|bedrock|stone|brick|obsidian|terracotta|blackstone|andesite|diorite|granite|magma|enchanting table|raw (iron|copper|gold)/, 'stone'],
  [/copper/, 'copper'],
  [/wool|carpet|\bbed\b|sponge/, 'cloth'],
  [/^snow( block)?$/, 'snow'],
  [/sand/, 'sand'],
  [/gravel|dirt|farmland|clay|podzol|mycelium|path/, 'gravel'],
  [/iron|gold|block of|rail|hopper|cauldron/, 'metal'],
  [/plank|log|wood|stem|door|chest|crafting|bookshelf|fence|trapdoor|sign|barrel|mushroom block|note|jukebox|campfire|torch|pumpkin|melon/, 'wood'],
  [/grass|leaves|leaf|litter|flower|sapling|fern|vine|hay|petals|bush|tulip|rose|dandelion|poppy|orchid|allium|bluet|daisy|lily|sugar cane|cactus|sunflower|lilac|peony|mushroom|tnt|fire|cornflower|wildflowers/, 'grass'],
];

export function materialOfName(name: string): Material {
  const n = name.toLowerCase();
  for (const [re, m] of RULES) if (re.test(n)) return m;
  return 'stone';
}

const cache = new Map<number, Material>();
export function materialOf(blockId: number): Material {
  let m = cache.get(blockId);
  if (m === undefined) {
    const def = BLOCKS[blockId];
    m = def ? materialOfName(def.name) : 'stone';
    cache.set(blockId, m);
  }
  return m;
}
